import { useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';

export interface VideoUploadResult {
  url: string;
  type: 'video';
}

interface VideoUploadProps {
  onUpload: (result: VideoUploadResult) => void;
  onError: (message: string) => void;
  onLoadingChange: (loading: boolean) => void;
  children: (trigger: () => void) => React.ReactNode;
}

const MAX_VIDEO_DURATION = 120; // 2 minutes in seconds
const MAX_VIDEO_SIZE = 200 * 1024 * 1024; // 200MB

export const VideoUpload = ({ onUpload, onError, onLoadingChange, children }: VideoUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { t } = useI18n();

  const trigger = () => inputRef.current?.click();

  const validateDuration = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const valid = video.duration <= MAX_VIDEO_DURATION;
        // Revoke after a tick so the browser finishes any pending decode
        setTimeout(() => URL.revokeObjectURL(url), 0);
        resolve(valid);
      };
      video.onerror = () => {
        setTimeout(() => URL.revokeObjectURL(url), 0);
        resolve(true); // allow upload if metadata can't be read; server will handle
      };
      video.src = url;
    });
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (file.size > MAX_VIDEO_SIZE) {
      onError(t('media.video_too_large'));
      return;
    }

    const isValidDuration = await validateDuration(file);
    if (!isValidDuration) {
      onError(t('media.video_too_long'));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    const ext = file.name.split('.').pop() ?? 'mp4';
    const path = `${user.id}/${Date.now()}.${ext}`;

    try {
      onLoadingChange(true);
      const { error } = await supabase.storage
        .from('post-media')
        .upload(path, file, { cacheControl: '3600', upsert: false });

      if (error) throw error;

      const { data } = supabase.storage.from('post-media').getPublicUrl(path);
      onUpload({ url: data.publicUrl, type: 'video' });
    } catch {
      onError(t('media.upload_error'));
    } finally {
      onLoadingChange(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={handleChange}
      />
      {children(trigger)}
    </>
  );
};
